import { useCallback, useEffect, useState } from 'react';

import { cfetch, BadStatusError } from '@confluence/network';

import {
	ALL_EXTERNAL_PRODUCT_CONNECTIONS,
	type ExternalProductConnection,
	SUGGESTION_SOURCE_BY_CONNECTION,
	type SuggestionGroup,
} from '../__types__/RemoteObject.ts';

type SuggestionsResponse = {
	suggestions: SuggestionGroup[];
};

export type UseFetchSuggestionsResult = {
	groups: SuggestionGroup[];
	isLoading: boolean;
	refetch: () => Promise<void>;
	isSuppressed: boolean;
};

const normalizeParentIds = (suggestions: SuggestionGroup[]): SuggestionGroup[] =>
	suggestions.map((group) => ({
		...group,
		parentId: group.parentId ?? null,
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		remoteObjects: group.remoteObjects.map((obj) => ({ ...obj, parentId: obj.parentId ?? null })),
	}));

export const useFetchSuggestions = (
	spaceKey?: string,
	sources?: ExternalProductConnection[],
): UseFetchSuggestionsResult => {
	const [groups, setGroups] = useState<SuggestionGroup[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSuppressed, setIsSuppressed] = useState(false);

	const sourcesKey = sources ? [...sources].sort().join(',') : '';

	const refetch = useCallback(async () => {
		if (!spaceKey) {
			setGroups([]);
			setIsLoading(false);
			return;
		}

		setIsLoading(true);

		try {
			const selected = (sourcesKey ? sourcesKey.split(',') : []) as ExternalProductConnection[];
			const params = new URLSearchParams();
			if (selected.length > 0 && selected.length < ALL_EXTERNAL_PRODUCT_CONNECTIONS.length) {
				selected.forEach((source) =>
					params.append('sources', SUGGESTION_SOURCE_BY_CONNECTION[source] ?? source),
				);
			}
			const queryString = params.toString();

			const response = await cfetch(
				`/wiki/rest/internal/1.0/spaces/${spaceKey}/remote-objects/suggestions${
					queryString ? `?${queryString}` : ''
				}`,
				{ method: 'GET' },
			);

			const data: SuggestionsResponse = await response.json();
			setGroups(normalizeParentIds(data.suggestions ?? []));
			setIsSuppressed(false);
		} catch (error) {
			if (BadStatusError.isBadStatusError(error) && error.response.status === 404) {
				setIsSuppressed(true);
			}
		} finally {
			setIsLoading(false);
		}
	}, [spaceKey, sourcesKey]);

	useEffect(() => {
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
		void refetch();
	}, [refetch]);

	return { groups, isLoading, refetch, isSuppressed };
};
